import { TypedDocumentNode, gql } from '@apollo/client';
import { ComponentProps, Fragment, ReactNode } from 'react';
import styled from 'styled-components';

import { So5FixtureEvent } from '@sorare/core/src/__generated__/globalTypes';
import { CollapsibleTileContainer } from '@sorare/core/src/atoms/layout/CollapsibleTileContainer';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { OnboardingHighlightWrapper } from '@sorare/core/src/components/onboarding/OnboardingHighlightWrapper';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { EmptyState } from 'components/fixture/EmptyState';
import { MatchCenterBanner } from 'components/fixture/MatchCenterBanner';
import { DiscoverNewCompetition } from 'components/league/DiscoverNewCompetition';
import { FeaturedBanner } from 'components/league/FeaturedBanner';
import { NoCardEntry } from 'components/league/NoCardEntry';
import { isFixtureLive, isFixtureOpened } from 'lib/so5';

import {
  PlayProFixturePage_nextSo5Fixture,
  PlayProFixturePage_so5Fixture,
} from './__generated__/index.graphql';

const NoCardEntryContainer = styled.div`
  width: 100%;
  max-width: 400px;
  margin: auto;
`;

const InlineLayout = styled(Horizontal)`
  flex-wrap: wrap;
  gap: 0;
  align-self: center;
  align-items: stretch;
  justify-content: center;
  row-gap: var(--double-unit);

  & > * {
    flex: 1;
    @media ${tabletAndAbove} {
      flex: 0;
    }
  }
`;

type Props = {
  extra?: ReactNode;
  so5Fixture: PlayProFixturePage_so5Fixture;
  nextSo5Fixture: Nullable<PlayProFixturePage_nextSo5Fixture>;
  leagueTeamNodes: ReactNode[];
  matchCenterBgImage?: ComponentProps<
    typeof MatchCenterBanner
  >['backgroundImage'];
  stacked?: boolean;
  withOnboarding?: boolean;
  showDiscoverNewCompetitionsTile?: boolean;
};

export const PlayProFixturePage = ({
  extra,
  so5Fixture,
  nextSo5Fixture,
  withOnboarding,
  leagueTeamNodes,
  matchCenterBgImage,
  stacked = true,
  showDiscoverNewCompetitionsTile,
}: Props) => {
  const isLaptopAndAbove = useIsLaptop();

  const {
    flags: { allowDailyFixtureNoCardRoute = false },
  } = useFeatureFlags();

  const so5LeaderboardGroupInterfaces = so5Fixture?.so5LeaderboardGroups;

  const featuredLeaderboardGroups =
    so5LeaderboardGroupInterfaces?.filter(
      so5LeaderboardGroup =>
        so5LeaderboardGroup.featured &&
        !so5LeaderboardGroup.mySo5LeaderboardContenders.length
    ) || [];

  if (!so5Fixture) return null;

  const isUpcoming = isFixtureOpened(so5Fixture);
  const isLive = isFixtureLive(so5Fixture);
  const isNextUpcoming = isUpcoming && nextSo5Fixture?.slug === so5Fixture.slug;

  const upcomingFixture = isUpcoming ? so5Fixture : nextSo5Fixture;

  const featuredBanners = featuredLeaderboardGroups.map(
    (so5LeaderboardGroupInterface, i) => (
      <FeaturedBanner
        key={`${so5LeaderboardGroupInterface.slug + i}`}
        so5LeaderboardGroupInterface={so5LeaderboardGroupInterface}
      />
    )
  );

  const NodesLayout = stacked ? Fragment : InlineLayout;
  const noCardRouteEnabled =
    so5Fixture.type === So5FixtureEvent.CLASSIC || allowDailyFixtureNoCardRoute;

  return (
    <>
      {isUpcoming && !!featuredLeaderboardGroups.length && (
        <>
          {!isLaptopAndAbove ? (
            <FullWidthContainer>
              <CollapsibleTileContainer>
                {featuredBanners}
              </CollapsibleTileContainer>
            </FullWidthContainer>
          ) : (
            <>
              {featuredLeaderboardGroups.length > 1 ? (
                <FullWidthContainer>
                  <Scrollable
                    itemToDisplay={1}
                    withMask
                    overhang
                    contained={!isLaptopAndAbove}
                    dots={featuredLeaderboardGroups.map(
                      so5LeaderboardGroupInterface =>
                        so5LeaderboardGroupInterface.displayName
                    )}
                    autoPlay={8000}
                  >
                    {featuredBanners}
                  </Scrollable>
                </FullWidthContainer>
              ) : (
                featuredBanners
              )}
            </>
          )}
        </>
      )}
      {extra}
      {!leagueTeamNodes.length ? (
        <OnboardingHighlightWrapper
          wrap={withOnboarding}
          highlightName="PlayProEmptyState"
        >
          <FullWidthContainer>
            <EmptyState
              so5Fixture={so5Fixture}
              upcomingFixtureSlug={upcomingFixture?.slug || ''}
            />
          </FullWidthContainer>
        </OnboardingHighlightWrapper>
      ) : (
        <>
          {matchCenterBgImage && isLive && (
            <MatchCenterBanner
              so5Fixture={so5Fixture}
              backgroundImage={matchCenterBgImage}
            />
          )}
          <NodesLayout>
            {leagueTeamNodes}
            {so5Fixture.type === So5FixtureEvent.DAILY &&
              showDiscoverNewCompetitionsTile && (
                <DiscoverNewCompetition
                  stacked={stacked}
                  so5Fixture={so5Fixture}
                />
              )}
          </NodesLayout>
        </>
      )}
      {noCardRouteEnabled && (isNextUpcoming || isLive) && (
        <FullWidthContainer>
          <NoCardEntryContainer>
            <NoCardEntry
              sport={so5Fixture.sport}
              eventType={so5Fixture.type}
              alwaysShow={isNextUpcoming}
            />
          </NoCardEntryContainer>
        </FullWidthContainer>
      )}
    </>
  );
};

PlayProFixturePage.fragments = {
  nextSo5Fixture: gql`
    fragment PlayProFixturePage_nextSo5Fixture on So5Fixture {
      slug
      aasmState
    }
  ` as TypedDocumentNode<PlayProFixturePage_nextSo5Fixture>,
  so5Fixture: gql`
    fragment PlayProFixturePage_so5Fixture on So5Fixture {
      slug
      type
      sport
      so5LeaderboardGroups {
        slug
        featured
        mySo5LeaderboardContenders {
          slug
        }
        ...FeaturedBanner_so5LeaderboardGroupInterface
      }
      ...isFixtureLive_so5Fixture
      ...isFixtureOpened_so5Fixture
      ...EmptyState_so5Fixture
      ...MatchCenterBanner_so5Fixture
      ...DiscoverNewCompetition_so5Fixture
    }
    ${FeaturedBanner.fragments.so5LeaderboardGroupInterface}
    ${isFixtureLive.fragments.so5Fixture}
    ${isFixtureOpened.fragments.so5Fixture}
    ${EmptyState.fragments.so5Fixture}
    ${MatchCenterBanner.fragments.so5Fixture}
    ${DiscoverNewCompetition.fragments.so5Fixture}
  ` as TypedDocumentNode<PlayProFixturePage_so5Fixture>,
};
